
import React from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { Check, X } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { approveEvent, fetchAllEvents } from "@/services/eventService";
import { Event } from "@/types";

export function EventApprovalList() {
  const { data: events = [], refetch } = useQuery({
    queryKey: ['events', 'all'],
    queryFn: fetchAllEvents,
  });

  const handleApprove = async (eventId: string) => {
    try {
      await approveEvent(eventId);
      toast.success("Event approved successfully");
      refetch();
    } catch (error) {
      console.error("Error approving event:", error);
      toast.error("Failed to approve event");
    }
  };

  const unapprovedEvents = events.filter(event => !event.approved);

  if (unapprovedEvents.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No events pending approval
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {unapprovedEvents.map((event) => (
        <Card key={event.id}>
          <CardHeader>
            <div className="flex justify-between items-start">
              <CardTitle>{event.title}</CardTitle>
              <Badge variant="outline" className="bg-yellow-100 text-yellow-800">
                Pending Approval
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">{event.description}</p>
              <div className="flex gap-4 text-sm">
                <span>
                  {format(new Date(event.start_date), "PPP")} - {format(new Date(event.end_date), "PPP")}
                </span>
                {event.category && (
                  <Badge variant="secondary">{event.category}</Badge>
                )}
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex justify-end gap-2">
            <Button 
              variant="outline" 
              size="sm"
              className="text-red-600 hover:text-red-700"
            >
              <X className="h-4 w-4 mr-1" />
              Reject
            </Button>
            <Button 
              size="sm"
              className="bg-eco-500 hover:bg-eco-600"
              onClick={() => handleApprove(event.id)}
            >
              <Check className="h-4 w-4 mr-1" />
              Approve
            </Button>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}
